/**
 * Normaliza URLs de ficheros servidos por el backend en `/uploads/*`.
 *
 * Problema típico en local con Docker:
 * - En BD puede quedar guardado `http://localhost:5173/uploads/...` (frontend) o dominios antiguos.
 * - Pero los estáticos los sirve el backend (p. ej. `http://localhost:3000/uploads/...`).
 *
 * Esta función fuerza el origen correcto (API origin = `VITE_API_URL` sin `/api`).
 */
export function getApiOriginForStatic() {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const s = String(base).replace(/\/api\/?$/, '');
  return s || 'http://localhost:3000';
}

export function resolveUploadsUrl(input) {
  if (input == null || input === '') return '';
  const apiOrigin = getApiOriginForStatic();
  const u = String(input).trim();

  // Preview local / data URI: no tocar
  if (u.startsWith('blob:') || u.startsWith('data:')) return u;

  // Absolute URL
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (parsed.pathname.startsWith('/uploads/')) {
        // Always serve uploads from the backend origin (even if stored with frontend origin)
        return `${apiOrigin}${parsed.pathname}${parsed.search || ''}`;
      }
      return u;
    } catch {
      return u;
    }
  }

  // Assets públicos del frontend (p. ej. /banda-background.jpg)
  if (u.startsWith('/') && !u.startsWith('/uploads/')) return u;

  // Relative variants
  if (u.startsWith('/uploads/')) return `${apiOrigin}${u}`;
  return `${apiOrigin}/uploads/${u}`;
}

/**
 * URL de logo/avatar de banda o cofradía (ya recortado en perfil).
 * Misma resolución que uploads; centralizado para uso en UI.
 */
export function resolveEntityLogoUrl(input) {
  return resolveUploadsUrl(input);
}

/**
 * URL de imagen de página de partitura con cache-bust tras recorte/rotación
 * (el fichero se sobrescribe in-place y el browser cachea la misma path).
 * @param {{ imagenUrl?: string|null, updatedAt?: string|Date|null }|null|undefined} pagina
 */
export function pageImageSrc(pagina) {
  if (!pagina?.imagenUrl) return '';
  const base = resolveUploadsUrl(pagina.imagenUrl);
  if (!base) return '';
  const raw = pagina.updatedAt;
  if (raw == null || raw === '') return base;
  const ts = raw instanceof Date ? raw.getTime() : Date.parse(String(raw));
  if (!Number.isFinite(ts)) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}v=${ts}`;
}
